<template>
  <div class="max-w-9xl mx-auto p-6">
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">
        {{ $t("message.pending_products.title") }}
      </h1>
    </div>

    <!-- Add New Product Section -->
    <div class="mb-6 bg-white shadow-md rounded-lg p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        {{ $t("message.pending_products.add_new_product") }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.pending_products.nkr_code") }}
          </label>
          <input
            v-model="newProduct.nkrCode"
            type="text"
            :placeholder="$t('message.pending_products.enter_nkr_code')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            @keyup.enter="addNewProduct"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.pending_products.quantity") }}
          </label>
          <input
            v-model.number="newProduct.requestedQuantity"
            type="number"
            min="1"
            :placeholder="$t('message.pending_products.quantity')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            @keyup.enter="addNewProduct"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.pending_products.reason") }}
          </label>
          <select
            v-model="newProduct.reason"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="" disabled>{{
              $t("message.pending_products.select_reason")
            }}</option>
            <option
              v-for="option in reasonOptions"
              :key="option"
              :value="option"
            >
              {{ $t(`message.pending_products.reasons.${option}`) }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.request_restock_modal.comment") }}
          </label>
          <input
            v-model="newProduct.comment"
            type="text"
            :placeholder="$t('message.request_restock_modal.enter_comment')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            @keyup.enter="addNewProduct"
          />
        </div>
        <div class="flex items-end">
          <button
            @click="addNewProduct"
            :disabled="!newProduct.nkrCode || !newProduct.reason || loading"
            class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="!loading">Add Product</span>
            <span v-else>Adding...</span>
          </button>
        </div>
      </div>
      <div
        v-if="addError"
        class="mt-3 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm"
      >
        {{ addError }}
      </div>
      <div
        v-if="addSuccess"
        class="mt-3 bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded text-sm"
      >
        {{ addSuccess }}
      </div>
    </div>

    <!-- Filters -->
    <div class="mb-6 bg-gray-50 p-4 rounded-lg">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        {{ $t("message.pending_products.filters") }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.pending_products.status") }}
          </label>
          <select
            v-model="filters.status"
            @change="loadPendingProducts"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{{ $t("message.pending_products.all") }}</option>
            <option value="reported">{{
              $t("message.pending_products.reported")
            }}</option>
            <option value="addressed">{{
              $t("message.pending_products.addressed")
            }}</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.pending_products.user") }}
          </label>
          <select
            v-model="filters.userId"
            @change="loadPendingProducts"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{{
              $t("message.pending_products.all_users")
            }}</option>
            <option v-for="user in uniqueUsers" :key="user.id" :value="user.id">
              {{ user.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("message.pending_products.search") }}
          </label>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('message.pending_products.search_placeholder')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div class="flex items-end">
          <button
            @click="clearFilters"
            class="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            {{ $t("message.pending_products.clear_filters") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div
      v-if="selectedProducts.length > 0"
      class="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-lg"
    >
      <div class="flex items-center justify-between flex-wrap gap-4">
        <div class="text-blue-800 font-medium">
          {{
            $t("message.pending_products.products_selected", {
              count: selectedProducts.length
            })
          }}
        </div>
        <div class="flex gap-2 flex-wrap">
          <button
            @click="bulkUpdateStatus('reported')"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            {{ $t("message.pending_products.mark_as_reported") }}
          </button>
          <button
            @click="bulkUpdateStatus('addressed')"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
          >
            {{ $t("message.pending_products.mark_as_addressed") }}
          </button>
          <button
            @click="clearSelection"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
          >
            {{ $t("message.pending_products.clear_selection") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Mark All Actions -->
    <div class="mb-6 bg-white shadow-md rounded-lg p-4">
      <h3 class="text-sm font-semibold text-gray-800 mb-3">
        {{ $t("message.pending_products.bulk_operations") }}
      </h3>
      <div class="flex gap-2 flex-wrap">
        <button
          @click="markAllProducts('reported', 'addressed')"
          :disabled="loading"
          class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 transition-colors text-sm"
        >
          {{ $t("message.pending_products.mark_all_reported_addressed") }}
        </button>
        <button
          @click="showExportModal = true"
          :disabled="loading"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 transition-colors text-sm ml-auto"
        >
          {{ $t("message.pending_products.export") }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading && !products.length"
      class="flex justify-center items-center py-12"
    >
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
      ></div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
    >
      <strong>Error:</strong> {{ error }}
    </div>

    <!-- Products Table -->
    <div
      v-else-if="filteredProducts.length > 0"
      class="bg-white shadow-md rounded-lg overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-2 text-left w-10">
                <input
                  type="checkbox"
                  @change="toggleSelectAll"
                  :checked="allSelected"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th
                @click="sort('nkrCode')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-28"
              >
                <div class="flex items-center">
                  Code
                  <span v-if="sortBy === 'nkrCode'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16"
              >
                Img
              </th>
              <th
                @click="sort('description')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100"
              >
                <div class="flex items-center">
                  Description
                  <span v-if="sortBy === 'description'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('requestedQuantity')"
                class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-16"
              >
                <div class="flex items-center justify-center">
                  Qty
                  <span v-if="sortBy === 'requestedQuantity'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('currentStock')"
                class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-16"
              >
                <div class="flex items-center justify-center">
                  Stock
                  <span v-if="sortBy === 'currentStock'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('availableStock')"
                class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-20"
              >
                <div class="flex items-center justify-center">
                  {{ $t("message.pending_products.available_stock") }}
                  <span v-if="sortBy === 'availableStock'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('status')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-28"
              >
                <div class="flex items-center">
                  Status
                  <span v-if="sortBy === 'status'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('reason')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-28"
              >
                <div class="flex items-center">
                  Reason
                  <span v-if="sortBy === 'reason'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('comment')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-48"
              >
                <div class="flex items-center">
                  Comment
                  <span v-if="sortBy === 'comment'" class="ml-1">
                    {{ sortOrder === "asc" ? "▲" : "▼" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('userName')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-32"
              >
                <div class="flex items-center">
                  User
                  <span v-if="sortBy === 'userName'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                @click="sort('createdAt')"
                class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-tight cursor-pointer hover:bg-gray-100 w-32"
              >
                <div class="flex items-center">
                  When
                  <span v-if="sortBy === 'createdAt'" class="ml-1">
                    {{ sortOrder === "asc" ? "↑" : "↓" }}
                  </span>
                </div>
              </th>
              <th
                class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="product in filteredProducts"
              :key="product.id"
              :class="{ 'bg-blue-50': isSelected(product.id) }"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-2 py-2">
                <input
                  type="checkbox"
                  :checked="isSelected(product.id)"
                  @change="toggleSelect(product.id)"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <div
                  class="text-xs font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                  @click="openProduct(product.nkrCode)"
                  :title="product.nkrCode"
                >
                  {{ product.nkrCode }}
                </div>
              </td>
              <td>
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.description"
                  class="h-14 w-14 object-cover rounded"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="h-10 w-10 bg-gray-200 rounded flex items-center justify-center"
                >
                  <span class="text-gray-400" style="font-size: 10px">N/A</span>
                </div>
              </td>
              <td class="px-3 py-2">
                <div
                  class="text-xs text-gray-900 truncate max-w-xs"
                  :title="product.description"
                >
                  {{ product.description }}
                </div>
              </td>
              <td class="px-2 py-2 text-center">
                <input
                  v-if="editingQuantity === product.id"
                  v-model.number="editQuantityValue"
                  type="number"
                  min="1"
                  @blur="saveQuantity(product.id)"
                  @keyup.enter="saveQuantity(product.id)"
                  @keyup.esc="cancelEditQuantity"
                  class="w-14 px-1 py-1 border border-gray-300 rounded text-center text-xs"
                  autofocus
                />
                <div
                  v-else
                  @click="
                    startEditQuantity(product.id, product.requestedQuantity)
                  "
                  class="text-xs text-blue-600 font-medium cursor-pointer hover:text-blue-800"
                >
                  {{ product.requestedQuantity }}
                </div>
              </td>
              <td class="px-2 py-2 text-center">
                <div
                  class="text-xs font-medium"
                  :class="
                    product.currentStock > 0 ? 'text-green-600' : 'text-red-600'
                  "
                >
                  {{ product.currentStock }}
                </div>
              </td>
              <td class="px-2 py-2 text-center">
                <div
                  class="text-xs font-medium"
                  :class="
                    product.availableStock > 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  "
                >
                  {{ product.availableStock }}
                </div>
              </td>
              <td class="px-3 py-2">
                <select
                  :value="product.status"
                  @change="updateProductStatus(product.id, $event.target.value)"
                  class="text-xs rounded-full px-2 py-1 font-medium border-0 focus:ring-2 focus:ring-blue-500 w-full"
                  :class="getStatusClass(product.status)"
                >
                  <option value="reported">{{
                    $t("message.pending_products.reported")
                  }}</option>
                  <option value="addressed">{{
                    $t("message.pending_products.addressed")
                  }}</option>
                </select>
              </td>
              <td class="px-3 py-2">
                <div class="text-xs text-gray-900" v-if="product.reason">
                  {{ $t(`message.pending_products.reasons.${product.reason}`) }}
                </div>
                <div class="text-xs text-gray-400" v-else>
                  -
                </div>
              </td>
              <td class="px-3 py-2">
                <div
                  class="text-xs text-gray-900 truncate max-w-xs"
                  :title="product.comment"
                >
                  {{ product.comment || "-" }}
                </div>
              </td>
              <td class="px-3 py-2">
                <div
                  class="text-xs text-gray-900 truncate"
                  :title="product.userName"
                >
                  {{ product.userName }}
                </div>
                <div
                  class="text-xs text-gray-500 truncate"
                  :title="product.userEmail"
                >
                  {{ product.userEmail }}
                </div>
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <div class="text-xs text-gray-900">
                  {{ product.createdAt | dateRelative }}
                </div>
              </td>
              <td class="px-2 py-2 text-center">
                <button
                  @click="deleteProduct(product.id)"
                  class="text-red-600 hover:text-red-800 transition-colors"
                  title="Delete product"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Summary -->
      <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div class="text-sm text-gray-700">
          Showing
          <span class="font-medium">{{ filteredProducts.length }}</span> of
          <span class="font-medium">{{ products.length }}</span> products
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!loading"
      class="bg-white shadow-md rounded-lg p-12 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        {{ $t("message.pending_products.no_products_found") }}
      </h3>
      <p class="text-gray-500">
        {{ $t("message.pending_products.add_first_product") }}
      </p>
    </div>

    <!-- Product Modal -->
    <BaseProductModal
      v-model="showProductModal"
      @closeProduct="openedProduct = ''"
      :code="openedProduct"
      @productUpdated="checkProductUpdate"
    />

    <!-- Export Modal -->
    <ExportPendingProductsModal
      :show="showExportModal"
      @close="showExportModal = false"
      :department-id="departmentId"
    />
  </div>
</template>

<script>
import {
  getPendingProducts,
  createPendingProduct,
  updatePendingProduct,
  bulkUpdatePendingProducts,
  markAllPendingProducts,
  deletePendingProduct
} from "@/api/store/PendingProductsService";
import ExportPendingProductsModal from "@/components/ExportPendingProductsModal.vue";

export default {
  name: "PendingProducts",
  components: {
    ExportPendingProductsModal
  },
  data() {
    return {
      products: [],
      loading: false,
      error: null,
      addError: null,
      addSuccess: null,
      filters: {
        status: "reported",
        userId: ""
      },
      searchQuery: "",
      sortBy: "createdAt",
      sortOrder: "desc",
      selectedProducts: [],
      editingQuantity: null,
      editQuantityValue: 1,
      newProduct: {
        nkrCode: "",
        requestedQuantity: 1,
        reason: "",
        comment: ""
      },
      reasonOptions: ["out_of_stock", "price", "other"],
      showProductModal: false,
      openedProduct: "",
      showExportModal: false
    };
  },
  computed: {
    departmentId() {
      return this.$route.params.departmentCode;
    },
    uniqueUsers() {
      const users = [];
      const seen = new Set();

      this.products.forEach(product => {
        if (!seen.has(product.userId)) {
          seen.add(product.userId);
          users.push({
            id: product.userId,
            name: product.userName || product.userEmail
          });
        }
      });

      return users.sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredProducts() {
      let filtered = [...this.products];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          product =>
            product.nkrCode.toLowerCase().includes(query) ||
            product.description.toLowerCase().includes(query)
        );
      }

      // Sort
      filtered.sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];

        // Handle null/undefined
        if (aVal === null || aVal === undefined) aVal = "";
        if (bVal === null || bVal === undefined) bVal = "";

        // String comparison
        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return this.sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });

      return filtered;
    },
    allSelected() {
      return (
        this.filteredProducts.length > 0 &&
        this.filteredProducts.every(product =>
          this.selectedProducts.includes(product.id)
        )
      );
    }
  },
  watch: {
    openedProduct(newVal) {
      if (!newVal) {
        this.showProductModal = false;
      }
    }
  },
  mounted() {
    this.loadPendingProducts();
  },
  methods: {
    async loadPendingProducts() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          departamento_id: this.departmentId
        };

        if (this.filters.status) {
          params.status = this.filters.status;
        }

        if (this.filters.userId) {
          params.user_id = this.filters.userId;
        }

        const response = await getPendingProducts(params);

        if (response.status) {
          this.products = response.data || [];
        } else {
          this.error = response.message || "Failed to load pending products";
        }
      } catch (err) {
        this.error = err.message || "An error occurred while loading products";
        console.error("Error loading pending products:", err);
      } finally {
        this.loading = false;
      }
    },
    async addNewProduct() {
      if (!this.newProduct.nkrCode) {
        this.addError = "NKR Code is required";
        return;
      }

      if (!this.newProduct.reason) {
        this.addError = "Reason is required";
        return;
      }

      this.loading = true;
      this.addError = null;
      this.addSuccess = null;

      try {
        const productData = {
          nkr_code: this.newProduct.nkrCode.trim(),
          departamento_id: this.departmentId,
          requested_quantity: this.newProduct.requestedQuantity || 1,
          reason: this.newProduct.reason,
          comment: this.newProduct.comment
        };

        const response = await createPendingProduct(productData);

        if (response.status) {
          this.addSuccess = response.message || "Product added successfully";
          this.newProduct.nkrCode = "";
          this.newProduct.requestedQuantity = 1;
          this.newProduct.reason = "";
          this.newProduct.comment = "";

          // Reload products
          await this.loadPendingProducts();

          // Clear success message after 3 seconds
          setTimeout(() => {
            this.addSuccess = null;
          }, 3000);
        } else {
          this.addError = response.message || "Failed to add product";
        }
      } catch (err) {
        this.addError =
          err.response?.data?.message || err.message || "An error occurred";
        console.error("Error adding pending product:", err);
      } finally {
        this.loading = false;
      }
    },
    async updateProductStatus(productId, newStatus) {
      this.loading = true;
      this.error = null;

      try {
        const response = await updatePendingProduct(productId, {
          status: newStatus
        });

        if (response.status) {
          // Update local state
          const product = this.products.find(p => p.id === productId);
          if (product) {
            product.status = newStatus;
          }
        } else {
          this.error = response.message || "Failed to update product status";
          // Reload to revert changes
          await this.loadPendingProducts();
        }
      } catch (err) {
        this.error = err.message || "An error occurred while updating status";
        console.error("Error updating product status:", err);
        // Reload to revert changes
        await this.loadPendingProducts();
      } finally {
        this.loading = false;
      }
    },
    async bulkUpdateStatus(newStatus) {
      if (this.selectedProducts.length === 0) return;

      this.loading = true;
      this.error = null;

      try {
        const response = await bulkUpdatePendingProducts({
          ids: this.selectedProducts,
          status: newStatus
        });

        if (response.status) {
          // Update local state
          this.selectedProducts.forEach(id => {
            const product = this.products.find(p => p.id === id);
            if (product) {
              product.status = newStatus;
            }
          });
          this.clearSelection();
        } else {
          this.error = response.message || "Failed to update products";
        }
      } catch (err) {
        this.error = err.message || "An error occurred during bulk update";
        console.error("Error in bulk update:", err);
      } finally {
        this.loading = false;
      }
    },
    async markAllProducts(fromStatus, toStatus) {
      const confirmMessage = `Are you sure you want to mark all "${fromStatus}" products as "${toStatus}"?`;

      if (!confirm(confirmMessage)) return;

      this.loading = true;
      this.error = null;

      try {
        const response = await markAllPendingProducts({
          from_status: fromStatus,
          to_status: toStatus,
          departamento_id: this.departmentId
        });

        if (response.status) {
          // Reload all products
          await this.loadPendingProducts();
        } else {
          this.error = response.message || "Failed to mark all products";
        }
      } catch (err) {
        this.error =
          err.message || "An error occurred during mark all operation";
        console.error("Error in mark all:", err);
      } finally {
        this.loading = false;
      }
    },
    async deleteProduct(productId) {
      if (!confirm("Are you sure you want to delete this product?")) return;

      this.loading = true;
      this.error = null;

      try {
        const response = await deletePendingProduct(productId);

        if (response.status) {
          // Remove from local state
          this.products = this.products.filter(p => p.id !== productId);
          // Remove from selection if selected
          this.selectedProducts = this.selectedProducts.filter(
            id => id !== productId
          );
        } else {
          this.error = response.message || "Failed to delete product";
        }
      } catch (err) {
        this.error = err.message || "An error occurred while deleting product";
        console.error("Error deleting product:", err);
      } finally {
        this.loading = false;
      }
    },
    startEditQuantity(productId, currentQuantity) {
      this.editingQuantity = productId;
      this.editQuantityValue = currentQuantity;
    },
    async saveQuantity(productId) {
      if (this.editQuantityValue < 1) {
        this.editQuantityValue = 1;
      }

      try {
        const response = await updatePendingProduct(productId, {
          requested_quantity: this.editQuantityValue
        });

        if (response.status) {
          // Update local state
          const product = this.products.find(p => p.id === productId);
          if (product) {
            product.requestedQuantity = this.editQuantityValue;
          }
        }
      } catch (err) {
        console.error("Error updating quantity:", err);
      } finally {
        this.editingQuantity = null;
      }
    },
    cancelEditQuantity() {
      this.editingQuantity = null;
    },
    toggleSelect(productId) {
      const index = this.selectedProducts.indexOf(productId);
      if (index > -1) {
        this.selectedProducts.splice(index, 1);
      } else {
        this.selectedProducts.push(productId);
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedProducts = [];
      } else {
        this.selectedProducts = this.filteredProducts.map(p => p.id);
      }
    },
    isSelected(productId) {
      return this.selectedProducts.includes(productId);
    },
    clearSelection() {
      this.selectedProducts = [];
    },
    clearFilters() {
      this.filters.status = "";
      this.filters.userId = "";
      this.searchQuery = "";
      this.loadPendingProducts();
    },
    sort(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortBy = field;
        this.sortOrder = "asc";
      }
    },
    getStatusClass(status) {
      const classes = {
        reported: "bg-blue-100 text-blue-800",
        addressed: "bg-green-100 text-green-800"
      };
      return classes[status] || "bg-gray-100 text-gray-800";
    },
    formatDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    formatDateCompact(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);

      return `${month}/${day}/${year}`;
    },
    handleImageError(event) {
      event.target.style.display = "none";
    },
    openProduct(nkrCode) {
      this.openedProduct = nkrCode;
      this.showProductModal = true;
    },
    checkProductUpdate() {
      // Reload products after product modal updates
      this.loadPendingProducts();
    }
  }
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
